import { msg } from '@lingui/core/macro';

import type { BatMetadataConfigEntry } from './common/types';
import { coreFields } from './common/coreFields';
import {
  beaCodeField,
  currencyField,
  currencyQuantityField,
} from './common/expenditureFields';

export const financeCorporateAssetMetadata = {
  displayName: msg({
    message: 'Finance: Corporate asset',
    context: 'Activity data schema name for finance corporate assets',
  }),
  fields: {
    ...coreFields,
    asset_id: {
      displayName: msg({
        message: 'Asset ID',
        context:
          'An identifier for the corporate asset, used to track and deduplicate estimates',
      }),
      externalDescription: msg({
        message:
          'An identifier for the corporate asset, used to track and deduplicate estimates',
        context: 'Asset ID',
      }),
    },
    bea_code: beaCodeField,
    building_area: {
      displayName: msg({
        message: 'Building area',
        context: 'The area of the building',
      }),
      externalDescription: msg({
        message: 'The area of the building',
        context: 'Building area',
      }),
    },
    building_area_unit: {
      displayName: msg({
        message: 'Building area unit',
        context: 'Unit of measurement for building area',
      }),
      externalDescription: msg({
        message: 'Unit of measurement for building area',
        context: 'Building area unit',
      }),
    },
    building_kind: {
      displayName: msg({
        message: 'Building kind',
        context: 'The type of building',
      }),
      externalDescription: msg({
        message: 'The type of building',
        context: 'Building kind',
      }),
    },
    cloud_provider: {
      displayName: msg({
        message: 'Cloud provider',
        context: 'The cloud service provider',
      }),
      externalDescription: msg({
        message: 'The cloud service provider',
        context: 'Cloud provider',
      }),
    },
    cloud_spend: {
      displayName: msg({
        message: 'Cloud spend',
        context: 'Total cloud computing spend',
      }),
      externalDescription: msg({
        message: 'Total cloud computing spend',
        context: 'Cloud spend',
      }),
    },
    currency: currencyField,
    currency_quantity: currencyQuantityField,
    eeio_model_kind: {
      displayName: msg({
        message: 'EEIO model kind',
        context: 'The kind of EEIO model',
      }),
      externalDescription: msg({
        message: 'The kind of EEIO model',
        context: 'EEIO model kind',
      }),
    },
    electricity_usage: {
      displayName: msg({
        message: 'Electricity usage',
        context: 'Total electricity consumption',
      }),
      externalDescription: msg({
        message: 'Total electricity consumption',
        context: 'Electricity usage',
      }),
    },
    electricity_usage_unit: {
      displayName: msg({
        message: 'Electricity usage unit',
        context: 'Unit of measurement for electricity usage',
      }),
      externalDescription: msg({
        message: 'Unit of measurement for electricity usage',
        context: 'Electricity usage unit',
      }),
    },
    flight_distance: {
      displayName: msg({
        message: 'Flight distance',
        context: 'Total flight distance traveled',
      }),
      externalDescription: msg({
        message: 'Total flight distance traveled',
        context: 'Flight distance',
      }),
    },
    flight_distance_unit: {
      displayName: msg({
        message: 'Flight distance unit',
        context: 'Unit of measurement for flight distance',
      }),
      externalDescription: msg({
        message: 'Unit of measurement for flight distance',
        context: 'Flight distance unit',
      }),
    },
    headcount: {
      displayName: msg({
        message: 'Headcount',
        context: 'Total number of employees',
      }),
      externalDescription: msg({
        message: 'Total number of employees',
        context: 'Headcount',
      }),
    },
    location_country: {
      displayName: msg({
        message: 'Country',
        context: 'The country where the company is located',
      }),
      externalDescription: msg({
        message: 'The country where the company is located',
        context: 'Country',
      }),
    },
    percent_employees_wfh: {
      displayName: msg({
        message: 'Percent employees working from home',
        context: 'Percentage of employees working from home',
      }),
      externalDescription: msg({
        message: 'Percentage of employees working from home',
        context: 'Percent employees working from home',
      }),
    },
    percent_renewable: {
      displayName: msg({
        message: 'Percent renewable',
        context: 'Percentage of energy from renewable sources',
      }),
      externalDescription: msg({
        message: 'Percentage of energy from renewable sources',
        context: 'Percent renewable',
      }),
    },
    should_include_downstream_scope3: {
      displayName: msg({
        message: 'Include downstream scope 3',
        context:
          'Flag determining whether downstream scope 3 activities are included',
      }),
      externalDescription: msg({
        message:
          'Flag determining whether downstream scope 3 activities are included',
        context: 'Include downstream scope 3',
      }),
    },
  },
} as const satisfies BatMetadataConfigEntry;
